import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'

import { FilterButton } from '@root/shared/ui-kit'

import { TableMakerCell } from '../../table-cell/table-maker-cell'

import type { TableMakerCellProps } from '../../table-cell/table-maker-cell'
import type { PairModel } from '@root/entities/pair'
import type { Trade } from '@root/entities/trade'
import type {
  TradesTableModel,
  TradeTableColumnId,
} from '@root/features/trade/tables/trades-table/atoms/reatom-trades-table'
import type { ClassNameProps } from '@root/shared/react'
import type { DataTableCellProps } from '@root/shared/ui-kit'

type TradeMakerCellProps = {
  pair: PairModel | undefined
  withTags?: boolean
} & ClassNameProps &
  DataTableCellProps<Trade.Trade, TradeTableColumnId> &
  Pick<TableMakerCellProps, 'aliasUserWallet' | 'showOwner'>

export const TradeMakerCell: React.FC<TradeMakerCellProps> = ({
  className,
  value,
  model,
  column,
  pair,
  withTags,
  aliasUserWallet,
  showOwner,
}) => {
  const [filters] = useAtom(model.filters)
  const setFilter = useAction(model.setColumnFilter)

  const [maker] = useAtom((ctx) => {
    const modelTyped = model as TradesTableModel
    const makersCache = ctx.spy(modelTyped.makersCache)
    return makersCache.get(value.maker.address)?.maker ?? value.maker
  })

  const isFilteredByThisMaker = useMemo(() => {
    const filter = filters[column.id]
    return (
      filter?.type === 'string' &&
      filter.operator === 'eq' &&
      filter.value === maker.address
    )
  }, [filters, column.id, maker.address])

  const handleFilter = () => {
    if (isFilteredByThisMaker) {
      setFilter(column.id, undefined)
      return
    }

    setFilter(column.id, {
      type: 'string',
      column: column.id,
      operator: 'eq',
      value: maker.address,
    })
  }

  return (
    <TableMakerCell
      pair={pair}
      maker={maker}
      network={value.network}
      pairAddress={value.pairAddress}
      className={className}
      childrenPosition="end"
      makerIconPosition="end"
      color="var(--row-color)"
      withTags={withTags}
      aliasUserWallet={aliasUserWallet}
      showOwner={showOwner}
    >
      {<FilterButton onClick={handleFilter} active={isFilteredByThisMaker} />}
    </TableMakerCell>
  )
}
